import * as Sentry from '@sentry/angular';
import { trigger } from '@angular/animations';
import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { INotification } from './models/INotification';
import { AuthService } from './services/auth/auth.service';
import { loadCalculationLinks, loadPlans, resetNotification, storeUserData, setNotification, removeUserData } from './store/actions/app/app.actions';
import { IAppState } from './store/reducers/app/app.reducer';
import { inOutAnimation, onlyOutAnimation } from './utils/animations/animations';
import { URLS_WITHOUT_FOOTER, URLS_WITHOUT_NAVBAR_OPTIONS } from './utils/constants';
import { DataLayerService } from './services/window/data-layer.service';
import { UserLogsService } from './services/userlogs/userlogs.service';
import { NetworkService } from './services/network/network.service';
import { ZendeskService } from '@app/services/zendesk/zendesk.service';
import { SeoService } from '@app/services/seo/seo.service';
import { environment } from 'src/environments/environment';
import { IUser } from './models/IUser';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.sass'],
  animations: [
    trigger('onlyOutAnimation', onlyOutAnimation),
    trigger('inOutAnimation', inOutAnimation)
  ]
})

export class AppComponent implements OnInit {
  showAuthModal = false;
  title = 'sismat-frontend-app';
  release = environment.sentry.release;

  urlsWithoutFooter = URLS_WITHOUT_FOOTER;
  urlsWithoutNavbarOptions = URLS_WITHOUT_NAVBAR_OPTIONS;

  mercadopagoData$: Observable<string> = this.store.select(
    (state) => state.app.mercadopagoData
  );

  notification$: Observable<INotification> = this.store.select(
    (state) => state.app.notification
  );

  user$: Observable<IUser> = this.store.select(
    (state) => state.app.user
  );

  notification = null;
  hasNetworkError$ = this.networkService.hasNetworkError$;
  location = window.location;

  constructor(
    private store: Store<{ app: IAppState }>,
    private authService: AuthService,
    private router: Router,
    private titleService: Title,
    private dataLayerService: DataLayerService,
    private userLogService: UserLogsService,
    private networkService: NetworkService,
    private zendeskService: ZendeskService,
    private seoService: SeoService
  ) {
    this.seoService.init();
    this.router.events.subscribe(event=> { 
        if (event instanceof NavigationEnd)
        {
          this.dataLayerService.logPageView({
            page_path: event.urlAfterRedirects,
            page_location: window.location.href,
            page_title: document.title
          });
        }
    });
  }

  async ngOnInit() {
    console.log('AppComponent getUser');
    this.authService.getUser().subscribe({
      next: async (user) => {
        if(user) {
          this.store.dispatch(storeUserData({ user }))
        } else {
          this.store.dispatch(removeUserData());
        }
      }, 
      error: (error) => {
      }
    });
    this.authService.resetUserSubject.subscribe({
      next: async () => {
        console.log('AppComponent resetUserSubject');
        const user  = await this.authService.getUser().pipe(take(1)).toPromise();
        this.store.dispatch(storeUserData({ user }))
      }, 
      error: (error) => {
        const notification : INotification = {
          title: 'Error',
          text: "Error al obtener la información del sistema. Por favor recargue la página e intente nuevamente",
          type: "danger"
        }

        this.store.dispatch(setNotification({ notification }));
      } 
    })

    this.store.dispatch(loadCalculationLinks());
    this.titleService.setTitle('Sismat | App de cómputo y presupuesto de obra en Argentina')

    this.user$
    .subscribe({
      next: (data) => {
        Sentry.setUser(data?.id ? { id: String(data.id) } : null);

        // Zendesk es el canal de soporte de todos menos de los suscriptos, que
        // tienen el asistente de Sismat en esa misma esquina. Un usuario
        // logueado sin suscripción lo sigue viendo.
        const usaAsistente = !!data?.can_use_ia && !!data?.is_suscribed;

        if (usaAsistente) {
          this.zendeskService.hide();
        } else {
          this.zendeskService.load();
        }
      },
      error: (error) => {
        console.log(error);
      }
    })

    this.mercadopagoData$
    .subscribe({
      next: (data) => {
        if(data && data['init_point']) {
          window.location.href = data['init_point'];
        }
      }, 
      error: (error) => {
        const notification : INotification = {
          title: 'Error',
          text: "Error al obtener la información de la suscripción. Por favor recargue la página e intente nuevamente",
          type: "danger"
        }

        this.store.dispatch(setNotification({ notification }));
      } 
    });

    this.notification$
    .subscribe({
      next: (data) => {
        if(data) {
          this.notification = data;
          setTimeout(() => {
            this.notification = null;
            this.store.dispatch(resetNotification());
          }, 5000)
        }
      }, 
      error: (error) => {
        const notification : INotification = {
          title: 'Error',
          text: "Error al obtener la información del sistema. Por favor recargue la página e intente nuevamente",
          type: "danger"
        }

        this.store.dispatch(setNotification({ notification }));
      } 
    });

    this.store.dispatch(loadPlans());
  }

  showFooter() {
    return !this.urlsWithoutFooter.some(url => this.router.url.includes(url))
  }

  showNavbarOptions() {
    return !this.urlsWithoutNavbarOptions.some(url => this.router.url.includes(url))
  }

  showFiscalData() {
    //return true or false only if its exactly the root page 
    return this.router.url === '/';    
  }
}
